import {AfterViewInit, ChangeDetectorRef, Component, Inject, Input, OnDestroy} from '@angular/core';
import {Store} from '@ngrx/store';
import {select} from '@ngrx/store';
import {Subject} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import {ISurveyPlaceDetails, IPlaceDetail} from '../../../models/survey/ISurveyContent';
import {SurveysCombinedReducerState} from '../../+state/interface';
import {selectSurveyConfig} from '../../+state';
import {ISurveyConfig} from '../../../models/survey/ISurveyConfig';
import {MemberDirectEnvService} from '@clients/member-direct/survey-common';
import {ENV_SERVICE} from '@clients/shared/config';

declare global {
    interface Window {
        google: any;
    }
}

@Component({
    selector: 'surveys-element-place-details',
    templateUrl: './element-place-details.component.html',
    styleUrls: ['./element-place-details.component.scss']
})
export class ElementPlaceDetailsComponent implements AfterViewInit, OnDestroy {
    @Input()
    public element: Partial<ISurveyPlaceDetails>;

    public showPlaceDetails = false;
    public surveyConfig: ISurveyConfig | null = null;
    private destroyed$ = new Subject<void>();
    public cssVariables: {[key: string]: string} = {};
    private map: any = null;
    private markers: any[] = [];

    constructor(
        private cdr: ChangeDetectorRef,
        private store: Store<SurveysCombinedReducerState>,
        @Inject(ENV_SERVICE) private envService: MemberDirectEnvService
    ) {
        this.store.pipe(
            select(selectSurveyConfig),
            takeUntil(this.destroyed$)
        ).subscribe((config: ISurveyConfig) => {
            this.surveyConfig = config;
            this.updateCssVariables();
            setTimeout(() => {
                try {
                    this.cdr.detectChanges();
                } catch (e) {
                    // Ignore errors during initialization
                }
            }, 0);
        });
    }

    protected updateCssVariables(): void {
        const theme = this.surveyConfig?.theme;
        const blue = (theme?.variables?.blue as string)
        const textGrey = (theme?.variables?.textGrey as string)
        const white = '#ffffff';
        const titleColor = '#414141';

        this.cssVariables = {
            '--gmp-mat-color-primary': blue,
            '--gmp-mat-color-positive': blue,
            '--gmp-mat-color-on-surface': titleColor,
            '--gmp-mat-color-surface': white,
            '--gmp-mat-color-on-primary': white,
            '--gmp-mat-color-on-surface-variant': textGrey,
            '--gmp-mat-color-secondary-container': blue,
            '--gmp-mat-color-on-secondary-container': white,
            '--gmp-mat-font-body-small': '16px',
            '--gmp-mat-font-title-small': '16px'
        };
    }

    ngAfterViewInit() {
        if (this.surveyConfig) {
            this.updateCssVariables();
        }
        if (this.placeIds && this.placeIds.length > 0) {
            this.loadGoogleMapsAPI();
        }
    }

    get placeIds(): string[] {
        const config = this.element?.config;
        if (!config || !config.places) return [];
        return config.places.map(p => p.placeId);
    }

    get places(): IPlaceDetail[] {
        const config = this.element?.config;
        if (!config || !config.places) return [];
        return config.places;
    }

    get googleMapsApiKey(): string {
        return this.envService?.googleConfig?.maps?.apiKey || '';
    }

    public getComponentCssClasses(id: string): string {
        return `survey-element surveys-element-place-details surveys-element-${id}`;
    }

    ngOnDestroy() {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    protected async loadGoogleMapsAPI(): Promise<void> {
        try {
            await this.waitForGoogleMaps();
            
            if (window.google?.maps?.importLibrary) {
                await window.google.maps.importLibrary('places');
            }

            await this.loadPlacesUIKitScript();
            
            this.showPlaceDetails = true;
            this.cdr.detectChanges();
            
            await new Promise(resolve => setTimeout(resolve, 100));
            
            await this.initializeMap();
        } catch (error) {
            console.error('Error loading Google Maps API:', error);
            this.showPlaceDetails = true;
            this.cdr.detectChanges();
        }
    }

    protected async initializeMap(): Promise<void> {
        try {
            await new Promise(resolve => setTimeout(resolve, 300));
            
            const mapElement = document.querySelector('.places-map-container');
            if (!mapElement || !window.google?.maps?.Map) {
                console.warn('Map element or Google Maps API not ready');
                return;
            }

            const places = this.places.filter(p => p.lat && p.lng && p.lat !== 0 && p.lng !== 0);
            if (places.length === 0) {
                console.warn('No places with coordinates found');
                return;
            }

            const mapOptions: any = {
                disableDefaultUI: true,
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [{ visibility: 'off' }]
                    }
                ]
            };

            if (places.length === 1) {
                const place = places[0];
                mapOptions.center = { lat: place.lat!, lng: place.lng! };
                mapOptions.zoom = 15;
            } else {
                const firstPlace = places[0];
                mapOptions.center = { lat: firstPlace.lat!, lng: firstPlace.lng! };
                mapOptions.zoom = 12;
            }

            this.map = new window.google.maps.Map(mapElement as HTMLElement, mapOptions);

            if (places.length > 1) {
                const bounds = new window.google.maps.LatLngBounds();
                places.forEach(place => {
                    if (place.lat && place.lng) {
                        const latLng = new window.google.maps.LatLng(place.lat, place.lng);
                        bounds.extend(latLng);
                    }
                });
                this.map.fitBounds(bounds, { top: 20, right: 20, bottom: 20, left: 20 });
            }

            this.markers = places
                .filter(place => place.lat && place.lng)
                .map(place => {
                    const marker = new window.google.maps.Marker({
                        position: { lat: place.lat!, lng: place.lng! },
                        map: this.map,
                        title: place.placeId
                    });
                    return marker;
                });
        } catch (error) {
            console.error('Error initializing map:', error);
        }
    }

    protected waitForGoogleMaps(): Promise<void> {
        return new Promise((resolve, reject) => {
            const MAX_WAIT_MS = 10000;
            const startTime = performance.now();

            const pollForImportLibrary = () => {
                if (window.google?.maps?.importLibrary) {
                    resolve();
                    return;
                }

                const elapsed = performance.now() - startTime;
                if (elapsed > MAX_WAIT_MS) {
                    reject(new Error('Google Maps API failed to load'));
                    return;
                }

                requestAnimationFrame(pollForImportLibrary);
            };

            if (window.google?.maps?.importLibrary) {
                resolve();
                return;
            }

            if (document.querySelector('script[src*="maps.googleapis.com"]')) {
                requestAnimationFrame(pollForImportLibrary);
                return;
            }

            this.loadGoogleMapsScriptDynamically();

            requestAnimationFrame(pollForImportLibrary);
        });
    }

    protected loadGoogleMapsScriptDynamically(): void {
        ((g) => {
            var h: any,
                a: any,
                k: any,
                p = 'The Google Maps JavaScript API',
                c = 'google',
                l = 'importLibrary',
                q = '__ib__',
                m = document,
                b: any = window;
            b = b[c] || (b[c] = {});
            var d: any = b.maps || (b.maps = {}),
                r = new Set(),
                e = new URLSearchParams(),
                u = () =>
                    h ||
                    (h = new Promise(async (f: any, n: any) => {
                        await (a = m.createElement('script'));
                        e.set('libraries', [...r] + '');
                        for (k in g)
                            e.set(
                                k.replace(
                                    /[A-Z]/g,
                                    (t: string) => '_' + t[0].toLowerCase()
                                ),
                                g[k]
                            );
                        e.set('callback', c + '.maps.' + q);
                        a.src =
                            `https://maps.${c}apis.com/maps/api/js?` + e;
                        d[q] = f;
                        a.onerror = () =>
                            (h = n(Error(p + ' could not load.')));
                        const nonceScript = m.querySelector('script[nonce]') as HTMLScriptElement;
                        a.nonce = nonceScript?.nonce || '';
                        m.head.append(a);
                    }));
            d[l]
                ? (d[l] = (f: any, ...n: any[]) =>
                      r.add(f) && u().then(() => d[l](f, ...n)))
                : (d[l] = (f: any, ...n: any[]) =>
                      r.add(f) && u().then(() => d[l](f, ...n)));
        })({
            key: this.googleMapsApiKey,
            v: 'weekly',
        });
    }

    protected async loadPlacesUIKitScript(): Promise<void> {
        return new Promise((resolve) => {
            if (document.querySelector('script[src*="place_ui.js"]')) {
                resolve();
                return;
            }

            if (customElements.get('gmp-place-details-compact')) {
                resolve();
                return;
            }

            const script = document.createElement('script');
            script.type = 'module';
            script.src = 'https://www.gstatic.com/maps-platform/place_ui/1.0/place_ui.js';
            script.onload = () => resolve();
            script.onerror = () => resolve();
            
            const nonceScript = document.querySelector('script[nonce]') as HTMLScriptElement;
            if (nonceScript?.nonce) {
                script.setAttribute('nonce', nonceScript.nonce);
            }
            
            document.head.appendChild(script);
        });
    }
}

